import React from "react";
import { Formik } from "formik";
import toast from "react-hot-toast";
import * as yup from "yup";
import FancyRadioInput from "../../../../components/form/fancy-radio-input";
import TextInput from "../../../../components/form/text-input";
import Switch from "../../../../components/form/switch";
import Modal from "../../../../components/modal";
import Button from "../../../../components/new-button";
import baseStyles from "../base.module.scss";
import { addEquipmentCategory } from "../../../../../api";
import useEquipmentStore, {
  fetchCategories,
} from "../../../../stores/equipment";

const schema = yup.object({
  category: yup.string().trim().required("* Category is required"),
  category_type: yup.string().required("* Category type is required"),
  description: yup.string().trim().required("* Description is required"),
  free_text: yup.string().trim().nullable(),
  free_text_enable: yup.boolean(),
});

export const EquipmentCategoryModal = ({
  open,
  onClose,
  onSubmit,
  isEdit = false,
  editSelectedCat = null,
}) => {
  const getInitialValues = () => {
    if (isEdit && editSelectedCat) {
      return {
        category: editSelectedCat.category || "",
        category_type: editSelectedCat.category_type || "terminal",
        description: editSelectedCat.description || "",
        free_text: editSelectedCat.free_text || "",
        free_text_enable: editSelectedCat.free_text_enabled === 1,
      };
    }

    return {
      category: "",
      category_type: "terminal",
      description: "",
      free_text: "",
      free_text_enable: false,
    };
  };

  const handleFormSubmit = async (values, { setSubmitting }) => {
    toast.loading(`${isEdit ? "Updating" : "Creating"} equipment category...`);

    try {
      const submitData = {
        ...values,
        free_text_enable: values.free_text_enable ? 1 : 0,
      };

      if (isEdit) {
        submitData.id = editSelectedCat.id;
      }

      const response = await addEquipmentCategory(submitData);
      toast.dismiss();

      if (response.status) {
        setSubmitting(false);
        toast.success(
          `Equipment category ${isEdit ? "updated" : "created"} successfully`,
        );
        fetchCategories();
        onClose();
      } else {
        setSubmitting(false);
        toast.error(
          response.message ||
            `There was a problem ${isEdit ? "updating" : "creating"} equipment category`,
        );

        if (response.errors) {
          console.error("Validation errors:", response.errors);
        }
      }
    } catch (error) {
      setSubmitting(false);
      toast.error(
        `There was a problem ${isEdit ? "updating" : "creating"} equipment category`,
      );
      console.error(
        `Error ${isEdit ? "updating" : "creating"} equipment category:`,
        error,
      );
    }
  };

  return (
    <Modal
      isOpen={open}
      onClose={onClose}
      header={`${isEdit ? "Edit" : "Add"} equipment category`}
      notCentered
      size="sm"
    >
      <Formik
        enableReinitialize
        validationSchema={schema}
        onSubmit={handleFormSubmit}
        initialValues={getInitialValues()}
        validateOnBlur={false}
        validateOnChange={true}
      >
        {({
          handleSubmit,
          handleChange,
          values,
          touched,
          errors,
          setFieldValue,
          isSubmitting,
        }) => (
          <form autoComplete="off" onSubmit={handleSubmit}>
            <div className={`${baseStyles.grid} ${baseStyles.gap2}`}>
              <div
                className={`${baseStyles.grid} ${baseStyles.gridCols1} ${baseStyles.gap2}`}
              >
                <TextInput
                  label="Category"
                  id="category"
                  name="category"
                  placeholder="Enter category name"
                  defaultValue={values.category}
                  onChange={handleChange}
                  touched={touched.category}
                  errored={errors.category}
                  error={errors.category}
                />
              </div>

              <div
                className={`${baseStyles.grid} ${baseStyles.gridCols1} ${baseStyles.gap2}`}
              >
                <div>
                  <label className={baseStyles.label}>Category type</label>
                  <div
                    className={`${baseStyles.flex} ${baseStyles.flexWrap} ${baseStyles.gap1}`}
                  >
                    {[
                      {
                        label: "Terminal",
                        value: "terminal",
                      },
                      {
                        label: "Gateway",
                        value: "gateway",
                      },
                      {
                        label: "Add on",
                        value: "add_on",
                      },
                    ].map((item) => (
                      <FancyRadioInput
                        key={`category_type_${item.value}`}
                        id={`category_type_${item.value}`}
                        name="category_type"
                        label={item.label}
                        checked={values.category_type === item.value}
                        onChange={(e) => {
                          setFieldValue("category_type", item.value);
                        }}
                      />
                    ))}
                  </div>
                  {touched.category_type && errors.category_type && (
                    <div className={baseStyles.error}>
                      {errors.category_type}
                    </div>
                  )}
                </div>
              </div>

              <div
                className={`${baseStyles.grid} ${baseStyles.gridCols1} ${baseStyles.gap2}`}
              >
                <TextInput
                  label="Description"
                  id="description"
                  name="description"
                  placeholder="Description"
                  defaultValue={values.description}
                  onChange={handleChange}
                  touched={touched.description}
                  errored={errors.description}
                  error={errors.description}
                />
              </div>

              <div
                className={`${baseStyles.grid} ${baseStyles.gridCols1} ${baseStyles.gap2}`}
              >
                <TextInput
                  label="Free Text"
                  id="free_text"
                  name="free_text"
                  placeholder="Free text"
                  defaultValue={values.free_text}
                  onChange={handleChange}
                  touched={touched.free_text}
                  errored={errors.free_text}
                  error={errors.free_text}
                />
              </div>

              <div
                className={`${baseStyles.grid} ${baseStyles.gridCols1} ${baseStyles.gap2}`}
              >
                <Switch
                  id="free_text_enable"
                  name="free_text_enable"
                  label="Other: Field enable?"
                  checked={values.free_text_enable}
                  onChange={(e) => {
                    setFieldValue("free_text_enable", e.target.checked);
                  }}
                />
              </div>
            </div>

            <div className={`${baseStyles.grid} ${baseStyles.gap2}`}>
              <div
                className={`${baseStyles.flex} ${baseStyles.flexEnd} ${baseStyles.gap1}`}
                style={{ marginTop: "16px" }}
              >
                <Button type="button" variant="ghost" onClick={onClose}>
                  Cancel
                </Button>
                <Button type="submit" disabled={isSubmitting}>
                  {isEdit ? "Update" : "Add"}
                </Button>
              </div>
            </div>
          </form>
        )}
      </Formik>
    </Modal>
  );
};

export default EquipmentCategoryModal;
